<script setup>
import { computed, useAttrs } from 'vue';

import HrIcon from '../primitives/HrIcon.vue';
import HrItem from '../primitives/HrItem.vue';
import HrItemLabel from '../primitives/HrItemLabel.vue';
import HrItemSection from '../primitives/HrItemSection.vue';
import HrSkeleton from '../primitives/HrSkeleton.vue';

/**
 * Single row for vertical lists — label, optional caption, leading icon or avatar, and trailing metadata.
 *
 * @status recommended
 * @group lists-tables
 * @useWhen Rendering a row in a list that may include an icon, supporting text, or trailing metadata
 * @avoidWhen Navigation destinations — use HrNavItem; menu entries — use HrMenuItem
 * @related HrNavItem
 * @related HrListGroup
 * @within hr-list
 * @a11y Pass `clickable` to enable ripple and pointer cursor; a clickable row announces as a button.
 * Supply aria-label via attrs when the label alone is insufficient. A `pending` row sets aria-busy and
 * stops being clickable, so a screen reader is not offered a row that has no content yet.
 */
defineOptions({ name: 'HrListItem' });

const emit = defineEmits({
  /** Fired when the item is activated (only meaningful with `clickable`). */
  click: null,
});

const props = defineProps({
  /**
   * Primary text label.
   * @replacesSlot default
   */
  label: {
    type: String,
    default: null,
  },
  /**
   * Supporting text rendered in a smaller, muted style.
   * Appears below the label by default; use captionAbove to flip the order.
   */
  caption: {
    type: String,
    default: null,
  },
  /**
   * Renders the caption above the label instead of below.
   * Useful for category/overline-style metadata above the headline.
   */
  captionAbove: {
    type: Boolean,
    default: false,
  },
  /**
   * Material icon name for the leading section.
   * Use the #leading slot for custom leading content.
   */
  icon: {
    type: String,
    default: null,
  },
  /**
   * When true, sizes the leading section for a 40dp avatar image (M3 avatar list item).
   * When false (default), sizes it for a 24dp icon (M3 icon list item).
   * In both cases the leading section uses Quasar's fixed-width auxiliary layout internally.
   */
  avatar: {
    type: Boolean,
    default: false,
  },
  /**
   * Trailing supporting text shown on the right side (e.g. metadata, counts, dates).
   * Use the #trailing slot for trailing icons, badges, or action controls.
   */
  trailingText: {
    type: String,
    default: null,
  },
  /**
   * Enables ripple effect and pointer cursor — set when the item triggers an action.
   *
   * `disable` overrides it: a disabled row neither ripples nor emits, so a caller passing both does
   * not have to reconcile them.
   */
  clickable: {
    type: Boolean,
    default: false,
  },
  /** Applies the M3 active-indicator highlight. When null (default) no active style is applied. */
  active: {
    type: Boolean,
    default: null,
  },
  /**
   * Root HTML element. Use `li` inside a `<ul>`, or `a` for items with an `href`.
   * @values div, li, a, label
   */
  tag: {
    type: String,
    default: 'div',
  },
  /**
   * Clamps the main label to this many lines with an ellipsis.
   * Useful in compact contexts like notification popups.
   */
  labelLines: {
    type: Number,
    default: null,
  },
  /** Indentation level for nested list items; each level adds a fixed pixel offset. */
  insetLevel: {
    type: Number,
    default: null,
  },
  /** Prevents interaction and applies a dimmed appearance. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Renders the item label in the error color role. */
  destructive: {
    type: Boolean,
    default: false,
  },
  /**
   * Renders the row in the M3 "segmented" style — a filled surface-container rounded card, so each
   * row reads as a discrete card at rest (e.g. reorderable / "liftable" lists).
   */
  segmented: {
    type: Boolean,
    default: false,
  },
  /**
   * Removes the segmented card's horizontal inset so it sits flush with its container.
   *
   * `segmented` insets each card by 8px, which is right for a list that owns its surface. It is
   * wrong wherever the list sits inside another layout — a form grid, say — where every neighboring
   * control is flush to the column and the inset reads as misalignment. Has no effect without
   * `segmented`.
   */
  flush: {
    type: Boolean,
    default: false,
  },
  /**
   * De-emphasises the row (archived / historical / inactive) via a lower tonal role rather than
   * opacity, so it reads as secondary while staying AA-legible.
   */
  muted: {
    type: Boolean,
    default: false,
  },
  /**
   * Renders the row as placeholders while its content is still arriving, in the row's own anatomy
   * rather than as a generic block, so nothing shifts when the real content lands.
   *
   * PLACEHOLDERS ONLY WHAT THE ROW OWNS: its label and caption lines, and a caption line only when
   * `caption` is set. Declare the shape the row will have even though the values are not known yet,
   * or the placeholder reserves the wrong height and the content jumps when it arrives.
   *
   * The leading section is left to whatever fills it, because this row cannot know its size. Pass
   * `pending` to the leading content instead: `HrAvatar` and `HrAssistChip` both have it, and each
   * reserves its own footprint from its own size token.
   *
   * The row stops being clickable and reports aria-busy, and its trailing content is suppressed:
   * a placeholder is not a target, and trailing metadata for a row nobody can identify yet is
   * noise. Prefer this to a spinner for a list whose length is already known.
   */
  pending: {
    type: Boolean,
    default: false,
  },
});

const attrs = useAttrs();

/*
 * Quasar hardcodes `role="listitem"` on every q-item while q-list carries no matching
 * `role="list"`, so a row that performs an action announces as static text in a list that is not
 * there. HrCard answers this the same way for a clickable card.
 *
 * Left alone where something truer already applies: a row that navigates, or one rendered as a tag
 * that carries its own meaning, is an anchor or wraps a form control. Bound conditionally for the
 * same reason, so those keep Quasar's role rather than having it stripped, and so a call site with
 * a role of its own — a select's options are `role="option"` — still wins through attrs.
 */
const actionRole = computed(() => {
  const navigates = (attrs.to !== undefined && attrs.to !== null) || attrs.href !== undefined;
  return props.clickable && props.tag === 'div' && !navigates ? { role: 'button' } : {};
});

function onClick(event) {
  if (props.pending || props.disable) {
    return;
  }

  emit('click', event);
} // onClick()
</script>

<template>
  <hr-item
    v-ripple="clickable && !disable && !pending"
    :clickable="clickable && !pending"
    :aria-busy="pending || undefined"
    :active="active"
    :tag="tag"
    v-bind="{ ...actionRole, ...(insetLevel !== null ? { insetLevel } : {}) }"
    :disable="disable"
    :destructive="destructive"
    :segmented="segmented"
    :flush="flush"
    :muted="muted"
    @click="onClick"
  >
    <hr-item-section
      v-if="$slots.leading || icon"
      avatar
      :class="avatar ? undefined : 'hr-list-item__leading--icon'"
    >
      <!-- @slot Custom leading content; renders in place of the `icon` prop. -->
      <slot name="leading">
        <hr-icon :name="icon" />
      </slot>
    </hr-item-section>

    <hr-item-section>
      <!-- One line per line the row actually has, driven by `caption` the same way the leading
           circle is driven by `avatar`. Reserving a caption line unconditionally makes a
           single-line row shrink when it resolves, which is the same shift in the other direction.
           Unequal widths so a column of them does not read as a table.

           WIDTHS ARE IN em, NOT PERCENT. A percentage resolves against the parent, and this row's
           parent is shrink-to-fit in some hosts, where the only content IS the placeholder. That
           circularity collapses to zero width and the row renders as two hairlines. em resolves
           against font size, so the placeholder is always about as wide as the text it stands in
           for, and the class below clamps it on a narrow row. -->
      <template v-if="pending">
        <hr-skeleton
          class="hr-list-item__placeholder"
          type="text"
          width="11em"
        />
        <hr-skeleton
          v-if="caption !== null"
          class="hr-list-item__placeholder"
          type="text"
          width="7em"
        />
      </template>
      <!-- @slot Main content; renders in place of `label` and `caption` when provided. -->
      <slot v-else>
        <hr-item-label
          v-if="caption !== null && captionAbove"
          caption
        >
          {{ caption }}
        </hr-item-label>
        <hr-item-label
          v-if="label !== null"
          v-bind="labelLines !== null ? { lines: labelLines } : {}"
          data-e2e="list-item-label"
        >
          {{ label }}
        </hr-item-label>
        <hr-item-label
          v-if="caption !== null && !captionAbove"
          caption
        >
          {{ caption }}
        </hr-item-label>
      </slot>
    </hr-item-section>

    <hr-item-section
      v-if="!pending && ($slots.trailing || trailingText !== null)"
      side
    >
      <!-- @slot Custom trailing content; renders in place of `trailingText` (e.g. icons, badges, action controls). -->
      <slot name="trailing">
        <hr-item-label caption>
          {{ trailingText }}
        </hr-item-label>
      </slot>
    </hr-item-section>
    <!-- @slot Inline menu or popover anchored to the item (e.g. a context menu). -->
    <slot
      v-if="!pending"
      name="menu"
    />
  </hr-item>
</template>

<style lang="scss" scoped>

/*
 * Quasar's avatar section defaults to 56px, which is the correct M3 width
 * for an avatar list item (40dp avatar + gap). For icon list items (24dp icon),
 * M3 calls for a narrower 40px section. We shrink it via this class which is
 * applied when avatar=false (the default).
 * Note: Quasar's q-item__section--avatar class will always be present on this
 * section — it is required for the fixed-width auxiliary layout to work.
 */
/* Inline width comes from the skeleton's own prop, so this only clamps it. */
.hr-list-item__placeholder {
  max-width: 100%;
}

:deep(.hr-list-item__leading--icon) {
  min-width: 40px;
  max-width: 40px;
}

</style>
